import React, { useState } from 'react';
import { X, ShoppingCart, Plus, Minus, Star, Package } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { getPriceData } from '../utils/currency';

// Placeholder for products without images
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNmZWY3ZWQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1zaXplPSI0MCIgZmlsbD0iI2Q5NzcwNiI+8J+Tpjwvc3ZnPg==';

const QuickViewModal = ({ product, isOpen, onClose }) => {
    const [quantity, setQuantity] = useState(1);
    const [imageError, setImageError] = useState(false);
    const { addToCart } = useCart();

    if (!isOpen || !product) return null;

    const hasPromotion = product.promotional_price && product.promotional_price < product.price;
    const hasDiscount = !hasPromotion && product.discount_percent && product.discount_percent > 0;
    const finalPrice = hasPromotion 
        ? product.promotional_price
        : hasDiscount 
            ? product.price * (1 - product.discount_percent / 100)
            : product.price;
    const isOnSale = hasPromotion || hasDiscount;

    const handleAddToCart = async () => {
        const success = await addToCart(product.product_id, quantity);
        if (success) {
            toast.success('Добавено в количката', {
                description: `${quantity}x ${product.name}`
            });
            onClose();
        } else {
            toast.error('Грешка при добавяне');
        }
    };

    return (
        <div 
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
        >
            <div 
                className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                >
                    <X className="h-5 w-5 text-slate-600" />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Image */}
                    <div className="relative aspect-square bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-700 dark:to-slate-600">
                        {product.image_url && !imageError ? (
                            <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-full h-full object-contain p-4"
                                onError={() => setImageError(true)}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-24 h-24 text-amber-300" />
                            </div>
                        )}
                        {isOnSale && (
                            <span className="absolute top-4 left-4 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-4 py-2 rounded-xl shadow-lg">
                                {hasPromotion 
                                    ? `-${Math.round((1 - product.promotional_price / product.price) * 100)}%`
                                    : `-${product.discount_percent}%`
                                }
                            </span>
                        )}
                        {product.is_new && (
                            <span className="absolute top-4 right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-bold px-4 py-2 rounded-xl shadow-lg">
                                Ново
                            </span>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-6 md:p-8 flex flex-col">
                        {/* Badges */}
                        <div className="flex items-center gap-2 mb-3">
                            {product.is_bestseller && (
                                <span className="inline-flex items-center text-xs font-semibold text-amber-700 bg-amber-100 px-3 py-1 rounded-full">
                                    <Star className="h-3 w-3 fill-amber-400 text-amber-400 mr-1" />
                                    Бестселър
                                </span>
                            )}
                            {product.stock > 0 ? (
                                <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">
                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                                    В наличност
                                </span>
                            ) : (
                                <span className="inline-flex items-center text-xs font-medium text-red-700 bg-red-100 px-3 py-1 rounded-full">
                                    Изчерпано
                                </span>
                            )}
                        </div>

                        {/* Title */}
                        <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-800 mb-4">
                            {product.name}
                        </h2>

                        {/* Price */}
                        <div className="flex flex-col gap-1 mb-4">
                            {isOnSale ? (
                                <>
                                    <div className="flex items-baseline gap-3">
                                        <span className="text-3xl font-bold text-red-600">
                                            {getPriceData(finalPrice).eurFormatted} €
                                        </span>
                                        <span className="text-lg text-slate-400 line-through">
                                            {getPriceData(product.price).eurFormatted} €
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <span className="text-3xl font-bold text-amber-700">
                                        {getPriceData(product.price).eurFormatted} €
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-slate-600 mb-6 line-clamp-3 flex-grow">
                            {product.description || 'Няма описание'}
                        </p>

                        {/* Quantity selector */}
                        <div className="flex items-center gap-4 mb-6">
                            <span className="text-slate-600 font-medium">Количество:</span>
                            <div className="flex items-center gap-2 bg-amber-50 rounded-xl p-1">
                                <button 
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-amber-100 transition-colors"
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <span className="w-12 text-center font-bold text-lg">{quantity}</span>
                                <button 
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-amber-100 transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3">
                            <Button 
                                onClick={handleAddToCart}
                                disabled={product.stock <= 0}
                                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg rounded-xl shadow-lg"
                            >
                                <ShoppingCart className="h-5 w-5 mr-2" />
                                Добави в количка
                            </Button>
                        </div>

                        {/* View full details link */}
                        <Link 
                            to={`/product/${product.product_id}`}
                            className="text-center text-amber-600 hover:text-amber-700 font-medium mt-4 transition-colors"
                            onClick={onClose}
                        >
                            Виж пълни детайли →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuickViewModal;
